import { createFileRoute } from "@tanstack/react-router";
import { site, pageUrl } from "@/data/site";
import { Breadcrumbs, PageHeader, Section, SectionHeading } from "@/components/site/section";
import { CtaLink } from "@/components/site/cta";
import { ContactForm } from "@/components/site/contact-form";
import { ProcessTimeline } from "@/components/site/final-cta";
import { track } from "@/lib/analytics";

const hours: Array<[string, string]> = [
  ["Monday", "10 am – 10 pm"],
  ["Tuesday", "10 am – 10 pm"],
  ["Wednesday", "10 am – 6 pm"],
  ["Thursday", "10 am – 10 pm"],
  ["Friday", "10 am – 8 pm"],
  ["Saturday", "2 pm – 10 pm"],
  ["Sunday", "2 pm – 4 pm"],
];


export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact Paul | Paragould, Arkansas | Paul's Pens n Things" },
      {
        name: "description",
        content: `Email ${site.email} or call ${site.phoneDisplay} to talk through a custom pen, turned gift or carved sign. Paul answers every message himself.`,
      },
      { property: "og:title", content: "Contact Paul directly" },
      { property: "og:description", content: "Email or phone the workshop in Paragould, Arkansas. No forms required." },
      { property: "og:url", content: pageUrl("/contact") },
    ],
    links: [{ rel: "canonical", href: pageUrl("/contact") }],
  }),
  component: Contact,
});

function Contact() {
  return (
    <>
      <Breadcrumbs trail={[{ label: "Home", to: "/" }, { label: "Contact" }]} />
      <PageHeader
        eyebrow="Contact"
        title="You will be talking to Paul."
        lede="Let us design and make a hand-crafted product that meets your needs. Tell us what you want or what you are looking for, and we will get back to you with some ideas as soon as possible."
      >
        <CtaLink to="/design-your-piece" arrow className="h-12">
          Design Your Piece
        </CtaLink>
      </PageHeader>

      <Section className="pt-0" labelledBy="contact-form-heading">
        <div className="shell grid gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:items-start">
          <div>
            <SectionHeading
              id="contact-form-heading"
              eyebrow="Send a message"
              title="Tell Paul what you have in mind."
              lede="Leave your email and a short description. Paul answers every message himself, usually within a day or two."
            />
            <div className="mt-8">
              <ContactForm />
            </div>
          </div>

          <div className="surface-card p-8">
            <p className="eyebrow">The workshop</p>
            <p className="mt-3 font-[family-name:var(--font-display)] text-2xl">{site.name}</p>
            <address className="mt-3 not-italic text-sm leading-relaxed text-muted-foreground">
              3003 Trowbridge Dr
              <br />
              Paragould, Arkansas 72450
              <br />
              United States
            </address>
            <a
              href={`tel:${site.phoneHref}`}
              onClick={() => track("click_phone", { location: "contact_details" })}
              className="mt-4 inline-block text-sm underline underline-offset-4"
            >
              {site.phoneDisplay}
            </a>

            <p className="eyebrow mt-8">Hours</p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Whenever I am awake, except when I go to church. I am typically available:
            </p>
            <dl className="mt-4 divide-y divide-hairline border-y border-hairline text-sm">
              {hours.map(([day, time]) => (
                <div key={day} className="flex items-center justify-between gap-4 py-2.5">
                  <dt className="font-semibold">{day}</dt>
                  <dd className="text-muted-foreground">{time}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </Section>


      <Section tone="paper" className="pt-0" labelledBy="contact-methods-heading">
        <div className="shell grid gap-8 lg:grid-cols-3">
          <h2 id="contact-methods-heading" className="sr-only">
            Ways to reach Paul
          </h2>
          <div className="surface-card p-8">
            <p className="eyebrow">Email</p>
            <a
              href={`mailto:${site.email}`}
              onClick={() => track("click_email", { location: "contact_page" })}
              className="mt-3 block break-words font-[family-name:var(--font-display)] text-2xl underline underline-offset-4"
            >
              {site.email}
            </a>
            <p className="mt-3 text-sm text-muted-foreground">
              Best for photos, sketches and detailed descriptions. Attach anything that helps explain the idea.
            </p>
          </div>
          <div className="surface-card p-8">
            <p className="eyebrow">Phone</p>
            <a
              href={`tel:${site.phoneHref}`}
              onClick={() => track("click_phone", { location: "contact_page" })}
              className="mt-3 block font-[family-name:var(--font-display)] text-2xl underline underline-offset-4"
            >
              {site.phoneDisplay}
            </a>
            <p className="mt-3 text-sm text-muted-foreground">
              Best when you want to talk an idea through. If Paul is at the lathe, leave a message and he will call
              back.
            </p>
          </div>
          <div className="surface-card p-8">
            <p className="eyebrow">Workshop</p>
            <p className="mt-3 font-[family-name:var(--font-display)] text-2xl">{site.locality}</p>
            <p className="mt-3 text-sm text-muted-foreground">
              Visits are by arrangement only. Paul also sells at the ASU Farmers Market on Saturday mornings from
              May through October — confirm the current schedule with him before travelling.
            </p>
          </div>
        </div>
      </Section>

      <Section labelledBy="contact-what-heading">
        <div className="shell grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
          <SectionHeading
            id="contact-what-heading"
            eyebrow="Make it easy"
            title="What to include in your first message."
            lede="None of this is required — Paul will ask if something is missing — but it speeds things up considerably."
          />
          <ul className="space-y-5">
            {[
              ["What the piece is", "A pen, a bottle stopper, a carved sign, a cornhole set, or something you have not seen elsewhere."],
              ["Who it is for", "The recipient and the occasion often decide the material and the personalization."],
              ["Any wording", "Exact spelling for names, dates or engraved text. Paul carves exactly what you send."],
              ["Materials or keepsakes", "Wood from a family property, a coin, a cartridge case — say so early so Paul can assess it."],
              ["Your timing", "Any date it needs to be ready by, so Paul can tell you honestly whether it is achievable."],
              ["A photo", "A reference image or a rough sketch is worth several paragraphs."],
            ].map(([title, body]) => (
              <li key={title} className="border-t border-hairline pt-4">
                <p className="font-semibold">{title}</p>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{body}</p>
              </li>
            ))}
          </ul>
        </div>
      </Section>

      <Section tone="paper" labelledBy="contact-process-heading">
        <div className="shell">
          <SectionHeading
            id="contact-process-heading"
            eyebrow="After you get in touch"
            title="What happens next."
          />
          <div className="mt-10">
            <ProcessTimeline />
          </div>
          <p className="mt-10 max-w-prose text-sm text-muted-foreground">
            Payment is only ever taken through a PayPal invoice Paul sends after you have agreed the design and the
            price. He will never ask for card details by email or phone.
          </p>
        </div>
      </Section>
    </>
  );
}
